import { Injectable, signal } from '@angular/core';
import { jwtDecode } from 'jwt-decode';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  private tokenKey: string = 'access_token';
  private roleKey: string = 'role';
  private nameKey: string = 'name';
  private emailKey: string = 'email';
  private modify_password: string = 'modify_password';
  private has_club: string = 'has_club';
  private clubsKey: string = 'gym_clubs';
  private selectedClubIdKey: string = 'selected_club_id';

  readonly tokenSignal = signal<string | null>(typeof window !== 'undefined' ? localStorage.getItem(this.tokenKey) : null);

  getToken(): string | null {
    return this.tokenSignal();
  }

  getRole(): string | null {
    return localStorage.getItem(this.roleKey);
  }

  getName(): string | null {
    return localStorage.getItem(this.nameKey);
  }

  getEmail(): string | null {
    return localStorage.getItem(this.emailKey);
  }

  saveToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
    this.tokenSignal.set(token);
    try {
      const decoded: any = jwtDecode(token);
      if (decoded && decoded.role) {
        localStorage.setItem(this.roleKey, decoded.role);
        localStorage.setItem(this.modify_password, decoded.modify_password);
      }
    } catch (error) {
      console.error('Error decoding token:', error);
    }
  }

  saveUser(user: any): void {
    if (user) {
      localStorage.setItem(this.nameKey, user.name || '');
      localStorage.setItem(this.emailKey, user.email || '');
      if (user.role) {
        localStorage.setItem(this.roleKey, user.role.name || '');
      } else if (user.role_id) {
        localStorage.setItem(this.roleKey, user.role_id.toString());
      }
    }
  }

  getUser(): any {
    return {
      name: this.getName(),
      email: this.getEmail(),
    };
  }

  clearAll(): void {
    localStorage.clear();
    this.tokenSignal.set(null);
  }

  saveClubs(clubs: any[]): void {
    localStorage.setItem(this.clubsKey, JSON.stringify(clubs));
  }

  getClubs(): any[] {
    const clubsJson = localStorage.getItem(this.clubsKey);
    if (!clubsJson) return [];
    try {
      return JSON.parse(clubsJson);
    } catch (e) {
      return [];
    }
  }

  saveSelectedClubId(clubId: number): void {
    localStorage.setItem(this.selectedClubIdKey, clubId.toString());
  }

  getSelectedClubId(): number {
    const selected = localStorage.getItem(this.selectedClubIdKey);
    if (selected) {
      return Number(selected);
    }
    const token = this.getToken();
    if (!token) return 0;
    try {
      const decoded: any = jwtDecode(token);
      return decoded.club_id || decoded.club || 0;
    } catch (error) {
      return 0;
    }
  }

  getClubId(): number {
    return this.getSelectedClubId();
  }

  removeToken(): void {
    localStorage.removeItem(this.tokenKey);
    this.tokenSignal.set(null);
    localStorage.removeItem(this.roleKey);
    localStorage.removeItem(this.nameKey);
    localStorage.removeItem(this.emailKey);
    localStorage.removeItem(this.clubsKey);
    localStorage.removeItem(this.selectedClubIdKey);
  }
}

